import { supabase, handleSupabaseError, withCompanyFilter, withCompanyFilterCamelCase, getCompanyId } from './supabase.js'
import { clientEmailService } from './emailService'
import { triggerQuickBooksSync } from './quickbooksSync'
import {
  DataServiceCore,
  // Client operations (Batch 4c)
  getClients as sharedGetClients,
  getClient as sharedGetClient,
  createClient as sharedCreateClient,
  getClientByEmail as sharedGetClientByEmail,
  getClientEmails as sharedGetClientEmails,
  bulkCreateClients as sharedBulkCreateClients,
  updateClient as sharedUpdateClient,
  deleteClient as sharedDeleteClient,
  // Job operations (Batch 4c)
  getJobs as sharedGetJobs,
  getJobsByClient as sharedGetJobsByClient,
  getJob as sharedGetJob,
  createJob as sharedCreateJob,
  updateJob as sharedUpdateJob,
  deleteJob as sharedDeleteJob,
  // Estimate operations (Batch 4d)
  getEstimates as sharedGetEstimates,
  getEstimate as sharedGetEstimate,
  getEstimatesByStatus as sharedGetEstimatesByStatus,
  getEstimatesByClient as sharedGetEstimatesByClient,
  updateEstimate as sharedUpdateEstimate,
  deleteEstimate as sharedDeleteEstimate,
  generateEstimateNumber as sharedGenerateEstimateNumber,
  initializeEstimateCounter as sharedInitializeEstimateCounter,
  // Invoice operations (Batch 4d)
  getInvoices as sharedGetInvoices,
  getInvoice as sharedGetInvoice,
  getInvoicesByStatus as sharedGetInvoicesByStatus,
  getInvoicesByClient as sharedGetInvoicesByClient,
  getInvoicesByEstimate as sharedGetInvoicesByEstimate,
  updateInvoice as sharedUpdateInvoice,
  updateInvoiceStatus as sharedUpdateInvoiceStatus,
  deleteInvoice as sharedDeleteInvoice,
} from '@prosperia/shared'

// Web-specific data service layer for Supabase operations
// Extends the shared DataServiceCore with web-specific functionality

// Data service layer for Supabase operations
// Extends shared DataServiceCore for core functionality (auth, company, caching)
class SupabaseDataService extends DataServiceCore {
  constructor() {
    super()
    // Web-specific initialization can go here
  }

  // ============================================
  // Web-specific Authentication Overrides
  // ============================================

  // Override signUp with web-specific redirect URL and tracking
  async signUp(email, password, userData = {}) {
    // Use shared signUp with web-specific options
    return super.signUp(email, password, userData, {
      // Web-specific: use window.location.origin for redirect
      redirectUrl: `${window.location.origin}/dashboard`,
      // Web-specific: legal acceptance handler
      onLegalAcceptance: async (userId, options) => {
        const { recordAllLegalAcceptances } = await import('./legalDocumentAcceptance')
        return recordAllLegalAcceptances(userId, options)
      },
      // Web-specific: Meta Pixel tracking
      onTrackSignUp: async (data) => {
        const { trackMetaPixelSignUp } = await import('./metaPixel')
        trackMetaPixelSignUp(data)
      },
    })
  }

  // Override signIn to use web-specific company creation
  async signIn(email, password) {
    const { createCompanyForUser } = await import('./supabase.js')
    return super.signIn(email, password, {
      // Web-specific company creation (handles sessionStorage for pending subscriptions)
      onCompanyCreation: createCompanyForUser,
    })
  }

  // ============================================
  // Client management (Batch 4c - uses shared functions)
  // ============================================
  async getClients(companyId = null) {
    return sharedGetClients({
      companyId,
      getCompanyId: () => this.getCompanyId(),
    })
  }

  async getClient(id) {
    const user = await this.getCurrentUser()
    return sharedGetClient(id, { user })
  }

  async createClient(clientData) {
    const user = await this.getCurrentUser()
    const result = await sharedCreateClient(clientData, { user })
    triggerQuickBooksSync('client', result?.id)
    return result
  }

  async getClientByEmail(email) {
    const user = await this.getCurrentUser()
    return sharedGetClientByEmail(email, { user })
  }

  async getClientEmails() {
    const user = await this.getCurrentUser()
    return sharedGetClientEmails({ user })
  }

  async bulkCreateClients(rows, { onProgress } = {}) {
    const user = await this.getCurrentUser()
    const result = await sharedBulkCreateClients(rows, { user, onProgress })
    result.created.forEach((client) => triggerQuickBooksSync('client', client?.id))
    return result
  }

  async updateClient(id, updates) {
    const user = await this.getCurrentUser()
    const result = await sharedUpdateClient(id, updates, { user })
    triggerQuickBooksSync('client', id)
    return result
  }

  async deleteClient(id) {
    const user = await this.getCurrentUser()
    return sharedDeleteClient(id, { user })
  }

  // Job management (Batch 4c - uses shared functions)
  async getJobs(companyId = null) {
    return sharedGetJobs({
      companyId,
      getCompanyId: () => this.getCompanyId(),
    })
  }

  async getJobsByClient(clientId) {
    const user = await this.getCurrentUser()
    return sharedGetJobsByClient(clientId, {
      user,
      // Pass caching function to shared module
      getCachedOrRateLimited: (key, fn) => this.getCachedOrRateLimited(key, fn),
    })
  }

  async getJob(id) {
    const user = await this.getCurrentUser()
    return sharedGetJob(id, { user })
  }

  async createJob(jobData) {
    return sharedCreateJob(jobData)
  }

  async updateJob(id, updates) {
    const user = await this.getCurrentUser()
    return sharedUpdateJob(id, updates, { user })
  }

  async deleteJob(id) {
    const user = await this.getCurrentUser()
    return sharedDeleteJob(id, { user })
  }

  // Estimate management
  // Only persist columns that exist on public.estimates
  static sanitizeEstimatePayload(raw) {
    if (!raw || typeof raw !== 'object') return {}

    const allowedKeys = new Set([
      'company_id',
      'client_id',
      'estimate_number',
      'title',
      'description',
      'estimate_type',
      'customer_name',
      'customer_email',
      'customer_phone',
      'customer_address',
      'subtotal',
      'discount_type',
      'tax_rate',
      'tax_amount',
      'deposit_amount',
      'final_price',
      'total_amount', // For backward compatibility
      'payment_terms',
      'valid_until',
      'requires_signature',
      'status',
      'sent_at',
      'viewed_at',
      'accepted_at',
      'pdf_url',
      // REMOVED: 'public_token' - now stored in magic_links table only
      'signature_data',
      'custom_branding',
      'created_at',
      'updated_at',
      'created_by',
      'discount_amount',
      'items',
      'google_maps_data',
      'map_snapshot_url',
      // Terms and conditions snapshot field
      'final_terms',
      // Line items optional flag
      'make_line_items_optional'
    ])

    const sanitized = {}
    for (const [key, value] of Object.entries(raw)) {
      if (allowedKeys.has(key)) sanitized[key] = value
    }
    return sanitized
  }
  // ============================================
  // Estimate management (Batch 4d - uses shared functions)
  // ============================================
  async getEstimates(passthroughCompanyId = null) {
    return sharedGetEstimates({
      companyId: passthroughCompanyId,
      getCompanyId: () => this.getCompanyId(),
    })
  }

  async getEstimatesByStatus(status) {
    return sharedGetEstimatesByStatus(status, {
      getCompanyId: () => this.getCompanyId(),
    })
  }

  async getEstimatesByClient(clientId) {
    const user = await this.getCurrentUser()
    return sharedGetEstimatesByClient(clientId, {
      user,
      getCachedOrRateLimited: (key, fn) => this.getCachedOrRateLimited(key, fn),
    })
  }

  async createEstimate(estimateData) {
    // Check connection health first (quick check, but don't fail if it times out)
    const companyId = estimateData.companyId || estimateData.company_id
    if (companyId) {
      try {
        // Quick health check - try a simple query with short timeout
        await Promise.race([
          supabase.from('companies').select('id').eq('id', companyId).limit(1).single(),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Connection check timeout')), 2000))
        ])
        console.log('[createEstimate] Connection health check passed')
      } catch (error) {
        console.warn('[createEstimate] Connection check failed, but proceeding:', error.message)
        // Continue anyway - the insert might still work
      }
    }

    // Create timeout promise
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error('Estimate creation timed out after 15 seconds. Please check your network connection.'))
      }, 15000)
    })

    // Create the actual operation promise
    const operationPromise = (async () => {
      console.log('[createEstimate] Starting estimate creation with data:', {
        ...estimateData,
        items: estimateData.items ? '[serialized]' : undefined,
        final_terms: estimateData.final_terms ? estimateData.final_terms.substring(0, 50) + '...' : undefined
      })
      
      // Map field names to match database schema
      const mappedData = {
        ...estimateData,
        company_id: estimateData.companyId || estimateData.company_id,
        client_id: estimateData.clientId || estimateData.client_id
      }
      
      // Remove the camelCase versions to avoid conflicts
      delete mappedData.companyId
      delete mappedData.clientId
      
      // REMOVED: Token expiration handling - tokens are now created via magic-link-generate edge function
      // Token expiration is stored in magic_links table, not in estimates table
      // If you need to set a token expiration, use MagicLinkService.generateMagicLink() after creating the estimate
      
      console.log('[createEstimate] Sanitizing payload...')
      const sanitized = SupabaseDataService.sanitizeEstimatePayload(mappedData)
      console.log('[createEstimate] Sanitized payload prepared, inserting into database...')
      
      const { data, error } = await supabase
        .from('estimates')
        .insert([sanitized])
        .select()
        .single()
      
      console.log('[createEstimate] Insert result:', { data: data?.id, error })
      
      if (error) {
        console.error('[createEstimate] Supabase error:', error)
        handleSupabaseError(error, 'create estimate')
      }
      
      if (!data) {
        console.error('[createEstimate] No data returned from insert')
        throw new Error('Failed to create estimate: No data returned from database')
      }
      
      console.log('[createEstimate] Estimate created successfully:', data.id)
      return data
    })()

    // Race between operation and timeout
    return await Promise.race([operationPromise, timeoutPromise])
  }

  async getEstimate(id) {
    const user = await this.getCurrentUser()
    return sharedGetEstimate(id, { user })
  }

  // Initialize estimate counter - delegates to shared function
  async initializeEstimateCounter(companyId) {
    const result = await sharedInitializeEstimateCounter(companyId)
    return result !== null
  }

  // Generate unique estimate number - delegates to shared function
  async generateEstimateNumber(companyId) {
    return sharedGenerateEstimateNumber(companyId)
  }

  // Get invoice PDF
  async getInvoicePDF(invoiceId) {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('No active session')

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-invoice-pdf`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({ invoice_id: invoiceId })
        }
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'PDF generation failed')
      }

      return await response.blob()
    } catch (error) {
      handleSupabaseError(error, 'get invoice PDF')
      return null
    }
  }

  async updateEstimate(id, updates) {
    const user = await this.getCurrentUser()
    // Use web-specific sanitization before delegating to shared
    const sanitized = SupabaseDataService.sanitizeEstimatePayload(updates)
    return sharedUpdateEstimate(id, sanitized, { user })
  }

  async deleteEstimate(id) {
    const user = await this.getCurrentUser()
    return sharedDeleteEstimate(id, { user })
  }


  // ============================================
  // Invoice management (Batch 4d - uses shared functions)
  // ============================================
  async getInvoices(passthroughCompanyId = null) {
    // Delegate to shared function
    const data = await sharedGetInvoices({
      companyId: passthroughCompanyId,
      getCompanyId: () => this.getCompanyId(),
    })

    // Convert snake_case to camelCase for frontend compatibility
    return (data || []).map(invoice => ({
      id: invoice.id,
      companyId: invoice.company_id,
      clientId: invoice.client_id,
      jobId: invoice.job_id,
      estimateId: invoice.estimate_id,
      total: invoice.total_amount,
      status: invoice.status,
      dueDate: invoice.due_date,
      notes: invoice.notes,
      createdAt: invoice.created_at,
      updatedAt: invoice.updated_at,
      paidAt: invoice.paid_at, // Include paid_at for frontend status determination
      paid_at: invoice.paid_at, // Also include snake_case for compatibility
      viewed_at: invoice.viewed_at, // Include viewed_at for completeness
      client: invoice.client,
      payments: invoice.payments
    }))
  }

  async getInvoicesByStatus(status) {
    const data = await sharedGetInvoicesByStatus(status, {
      getCompanyId: () => this.getCompanyId(),
    })

    // Convert snake_case to camelCase for frontend compatibility
    return (data || []).map(invoice => ({
      id: invoice.id,
      companyId: invoice.company_id,
      clientId: invoice.client_id,
      jobId: invoice.job_id,
      estimateId: invoice.estimate_id,
      total: invoice.total_amount,
      status: invoice.status,
      dueDate: invoice.due_date,
      notes: invoice.notes,
      createdAt: invoice.created_at,
      updatedAt: invoice.updated_at,
      paidAt: invoice.paid_at,
      paid_at: invoice.paid_at,
      viewed_at: invoice.viewed_at,
      client: invoice.client
    }))
  }

  async getInvoicesByClient(clientId) {
    const user = await this.getCurrentUser()
    const data = await sharedGetInvoicesByClient(clientId, {
      user,
      getCachedOrRateLimited: (key, fn) => this.getCachedOrRateLimited(key, fn),
    })

    // Convert snake_case to camelCase for frontend compatibility
    return (data || []).map(invoice => ({
      id: invoice.id,
      companyId: invoice.company_id,
      clientId: invoice.client_id,
      jobId: invoice.job_id,
      estimateId: invoice.estimate_id,
      total: invoice.total_amount,
      status: invoice.status,
      dueDate: invoice.due_date,
      notes: invoice.notes,
      createdAt: invoice.created_at,
      updatedAt: invoice.updated_at,
      paidAt: invoice.paid_at,
      paid_at: invoice.paid_at,
      viewed_at: invoice.viewed_at,
      client: invoice.client
    }))
  }

  async getInvoicesByEstimate(estimateId) {
    const data = await sharedGetInvoicesByEstimate(estimateId, {
      getCompanyId: () => this.getCompanyId(),
    })

    // Convert snake_case to camelCase for frontend compatibility
    return (data || []).map(invoice => ({
      id: invoice.id,
      companyId: invoice.company_id,
      clientId: invoice.client_id,
      jobId: invoice.job_id,
      estimateId: invoice.estimate_id,
      total: invoice.total_amount,
      status: invoice.status,
      dueDate: invoice.due_date,
      notes: invoice.notes,
      createdAt: invoice.created_at,
      updatedAt: invoice.updated_at,
      paidAt: invoice.paid_at,
      paid_at: invoice.paid_at,
      viewed_at: invoice.viewed_at,
      client: invoice.client
    }))
  }

  async getInvoicePhotos(passthroughCompanyId = null) {
    try {
      const user = await this.getCurrentUser()
      if (!user && !passthroughCompanyId) throw new Error('User not authenticated')
      
      const companyId = passthroughCompanyId || await getCompanyId(user)
      const { data, error } = await withCompanyFilter(
        supabase.from('invoice_photos').select('*'),
        companyId
      )
      
      if (error) throw error
      
      // Convert snake_case to camelCase for frontend compatibility
      return (data || []).map(photo => ({
        id: photo.id,
        companyId: photo.company_id,
        invoiceId: photo.invoice_id,
        filePath: photo.file_path,
        fileName: photo.file_name,
        fileSize: photo.file_size,
        mimeType: photo.mime_type,
        description: photo.description,
        photoType: photo.photo_type,
        displayOrder: photo.display_order,
        createdBy: photo.created_by,
        createdAt: photo.created_at,
        updatedAt: photo.updated_at
      }))
    } catch (error) {
      handleSupabaseError(error, 'get invoice photos')
      return []
    }
  }

  async createInvoice(invoiceData) {
    try {
      // Atomic, year-scoped invoice number generator. Server-side RPC handles
      // counter persistence and self-seeds from existing rows, so concurrent
      // callers can't collide.
      const nextInvoiceNumberFromRpc = async (companyId) => {
        const { data, error } = await supabase
          .rpc('next_invoice_number', { p_company_id: companyId })
        if (error) throw error
        return data
      }

      // Convert camelCase to snake_case for Supabase schema
      // Only include columns that exist in the actual database schema
      const convertedData = {
        company_id: invoiceData.companyId,
        client_id: invoiceData.clientId,
        job_id: invoiceData.jobId, // Link invoice to job
        estimate_id: invoiceData.estimateId, // Link invoice to estimate
        total_amount: invoiceData.total,
        status: invoiceData.status,
        due_date: invoiceData.dueDate,
        description: invoiceData.description || invoiceData.notes, // Use description field from database
        items: Array.isArray(invoiceData.items) ? invoiceData.items.map(it => ({
          description: String(it.description || ''),
          quantity: Number(it.quantity || 0),
          unit_price: Math.round(((Number(it.unitPrice || it.unit_price || it.rate || 0)) * 100)) / 100
        })) : null
      }
      
      // Add tax and deposit information if provided (from estimate)
      if (invoiceData.subtotal !== undefined) convertedData.subtotal = invoiceData.subtotal
      if (invoiceData.taxAmount !== undefined) convertedData.tax_amount = invoiceData.taxAmount
      if (invoiceData.taxRate !== undefined) {
        // Store tax_rate as percentage (e.g., 8 for 8%)
        // No conversion needed - already in percentage format
        convertedData.tax_rate = invoiceData.taxRate
      }
      if (invoiceData.depositAmount !== undefined && invoiceData.depositAmount > 0) {
        convertedData.deposit_amount = invoiceData.depositAmount
      }
      
      // Add pass-through fee fields
      if (invoiceData.servicesSubtotal !== undefined) {
        convertedData.services_subtotal = invoiceData.servicesSubtotal
      }
      if (invoiceData.clientPaymentAmount !== undefined) {
        convertedData.client_payment_amount = invoiceData.clientPaymentAmount
      }
      if (invoiceData.processingFeesAmount !== undefined) {
        convertedData.processing_fees_amount = invoiceData.processingFeesAmount
      }
      
      // Add recurring invoice fields
      if (invoiceData.isRecurring !== undefined) {
        convertedData.is_recurring = invoiceData.isRecurring
      }
      if (invoiceData.recurringFrequency) {
        convertedData.recurring_frequency = invoiceData.recurringFrequency
        convertedData.recurring_interval_count = invoiceData.recurringIntervalCount || 1
        convertedData.recurring_start_date = invoiceData.recurringStartDate
        convertedData.recurring_end_date = invoiceData.recurringEndDate
        convertedData.recurring_next_run_date = invoiceData.recurringNextRunDate
        convertedData.recurring_parent_id = invoiceData.recurringParentId
        convertedData.recurring_sequence = invoiceData.recurringSequence || 1
        convertedData.recurring_require_approval = invoiceData.recurringRequireApproval ?? true
        if (invoiceData.recurringJobTime !== undefined) convertedData.recurring_job_time = invoiceData.recurringJobTime
        if (invoiceData.recurringJobDurationMinutes !== undefined) convertedData.recurring_job_duration_minutes = invoiceData.recurringJobDurationMinutes
        if (invoiceData.recurringAssignedEmployeeIds !== undefined) convertedData.recurring_assigned_employee_ids = invoiceData.recurringAssignedEmployeeIds
        if (invoiceData.recurringAssignedTeamId !== undefined) convertedData.recurring_assigned_team_id = invoiceData.recurringAssignedTeamId
      }
      // Add rebill tracking
      if (invoiceData.rebilledAt || invoiceData.rebilled_at) {
        convertedData.rebilled_at = invoiceData.rebilledAt || invoiceData.rebilled_at
      }
      
      // Add required fields
      if (invoiceData.invoiceNumber) convertedData.invoice_number = invoiceData.invoiceNumber
      if (invoiceData.title) convertedData.title = invoiceData.title

      // invoice_number is NOT NULL with no default. If the caller didn't supply
      // one, mint it now via the atomic RPC so the insert can succeed.
      if (!convertedData.invoice_number) {
        convertedData.invoice_number = await nextInvoiceNumberFromRpc(invoiceData.companyId)
      }

      // Legal ToS snapshot — write whatever the caller computed from
      // company_branding.invoice_terms_*. Accept both snake_case and camelCase.
      if (invoiceData.final_terms !== undefined) {
        convertedData.final_terms = invoiceData.final_terms
      } else if (invoiceData.finalTerms !== undefined) {
        convertedData.final_terms = invoiceData.finalTerms
      }
      
      // Add photo fields if provided
      if (invoiceData.beforePhotos && Array.isArray(invoiceData.beforePhotos)) {
        convertedData.before_photos = invoiceData.beforePhotos
        convertedData.has_photos = true
      }
      if (invoiceData.afterPhotos && Array.isArray(invoiceData.afterPhotos)) {
        convertedData.after_photos = invoiceData.afterPhotos
        convertedData.has_photos = true
      }
      
      // REMOVED: Token expiration handling - tokens are now created via magic-link-generate edge function
      // Token expiration is stored in magic_links table, not in invoices table
      // If you need to set a token expiration, use MagicLinkService.generateMagicLink() after creating the invoice
      
      // Add created_by field (required)
      const { data: { session } } = await supabase.auth.getSession()
      if (session?.user?.id) {
        convertedData.created_by = session.user.id
      } else {
        throw new Error('User not authenticated')
      }
      
      // Try to insert with retry logic for duplicate invoice numbers
      let data, error
      let attempts = 0
      const maxAttempts = 3
      
      while (attempts < maxAttempts) {
        const { data: result, error: insertError } = await supabase
          .from('invoices')
          .insert([convertedData])
          .select()
          .single()
        
        error = insertError
        data = result
        
        // If we get a unique constraint violation (duplicate invoice number), retry with new number
        if (error?.code === '23505' && error?.message?.includes('invoices_number_company_unique') && attempts < maxAttempts - 1) {
          console.log(`Duplicate invoice number detected (attempt ${attempts + 1}/${maxAttempts}), generating new number...`)
          convertedData.invoice_number = await nextInvoiceNumberFromRpc(invoiceData.companyId)
          attempts++
          continue
        }
        
        // If no error or non-retryable error, break
        break
      }
      
      if (error) throw error
      
      // Note: public_token is now only created via magic-link-generate edge function
      // Do NOT create it during invoice creation - this ensures consistency with estimates
      // The public_token will be set when the invoice is sent to a client

      // Convert snake_case to camelCase for frontend compatibility
      triggerQuickBooksSync('invoice', data.id)
      return {
        id: data.id,
        companyId: data.company_id,
        clientId: data.client_id,
        jobId: data.job_id,
        estimateId: data.estimate_id,
        // REMOVED: publicToken and tokenExpiresAt - now stored in magic_links table
        // Use MagicLinkService to get/create magic links
        viewedAt: data.viewed_at,
        viewCount: data.view_count || 0,
        items: Array.isArray(data.items) ? data.items : [],
        total: data.total_amount,
        status: data.status,
        dueDate: data.due_date,
        notes: data.notes,
        createdAt: data.created_at,
        updatedAt: data.updated_at
      }
    } catch (error) {
      handleSupabaseError(error, 'create invoice')
      return null // Return null on error
    }
  }

  async getInvoice(id) {
    const user = await this.getCurrentUser()
    const data = await sharedGetInvoice(id, { user })

    if (!data) return null

    // Convert snake_case to camelCase for frontend compatibility
    return {
      id: data.id,
      companyId: data.company_id,
      clientId: data.client_id,
      jobId: data.job_id,
      estimateId: data.estimate_id,
      items: Array.isArray(data.items) ? data.items : [],
      total: data.total_amount,
      subtotal: data.subtotal || 0,
      taxAmount: data.tax_amount || 0,
      taxRate: data.tax_rate || 0,
      depositAmount: data.deposit_amount || 0,
      // Fields needed to hydrate the invoice edit form (draft editing)
      invoiceNumber: data.invoice_number,
      discountAmount: data.discount_amount || 0,
      discountType: data.discount_type || 'fixed',
      servicesSubtotal: data.services_subtotal || 0,
      customerName: data.customer_name,
      customerEmail: data.customer_email,
      customerPhone: data.customer_phone,
      customerAddress: data.customer_address,
      paymentTerms: data.payment_terms,
      isRecurring: data.is_recurring || false,
      recurringFrequency: data.recurring_frequency,
      recurringIntervalCount: data.recurring_interval_count,
      recurringEndDate: data.recurring_end_date,
      recurringRequireApproval: data.recurring_require_approval,
      status: data.status,
      dueDate: data.due_date,
      notes: data.notes,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      client: data.client,
      job: data.job,
      estimate: data.estimate,
      payments: data.payments
    }
  }

  async updateInvoice(id, updates) {
    const user = await this.getCurrentUser()
    // Shared function handles both camelCase and snake_case input
    const data = await sharedUpdateInvoice(id, updates, { user })

    if (!data) return null

    triggerQuickBooksSync('invoice', id)

    // Convert snake_case to camelCase for frontend compatibility
    return {
      id: data.id,
      companyId: data.company_id,
      clientId: data.client_id,
      jobId: data.job_id,
      estimateId: data.estimate_id,
      total: data.total_amount,
      status: data.status,
      dueDate: data.due_date,
      notes: data.notes,
      createdAt: data.created_at,
      updatedAt: data.updated_at
    }
  }

  async updateInvoiceStatusOnly(id, status) {
    const user = await this.getCurrentUser()
    const result = await sharedUpdateInvoiceStatus(id, status, { user })
    triggerQuickBooksSync('invoice', id)
    return result
  }

  async deleteInvoice(id) {
    const user = await this.getCurrentUser()
    return sharedDeleteInvoice(id, { user })
  }

  // DEPRECATED: getInvoiceByToken - removed in favor of magic-link-validate edge function
  // This method queried invoices.public_token directly, which created dual-storage issues
  // All invoice token lookups should now use MagicLinkService.validateMagicLink() 
  // or the useMagicLinkValidation hook, which queries the magic_links table (single source of truth)
  
  // If you see this method being used, it needs to be replaced with:
  // const { data } = await MagicLinkService.validateMagicLink(token)
  // const invoice = data.entity // This will be the invoice if type === 'invoice'

  // Start a Stripe Checkout session for an invoice
  async createInvoiceCheckoutSession(invoice, options = {}) {
    try {
      // Get session if available (for authenticated users)
      const { data: { session } } = await supabase.auth.getSession()
      
      // Check if company has Connect account enabled
      const companyId = invoice.company_id || invoice.companyId
      
      // publicToken indicates this is a public invoice - skip frontend checks, let edge function handle it
      const publicToken = options.publicToken || invoice.publicToken || null
      const isPublicInvoice = !!publicToken
      
      // For authenticated users accessing non-public invoices, check Connect account
      // For public invoices, skip frontend check and let edge function (with service role) handle validation
      if (session && !isPublicInvoice) {
        // Check payment_processing_enabled from companies table (more reliable - accounts for restrictions)
        // This field is updated by webhooks and considers charges_enabled, capability status, and other factors
        const { data: company, error: companyError } = await supabase
          .from('companies')
          .select('stripe_connect_account_id, payment_processing_enabled')
          .eq('id', companyId)
          .single()

        // Require Connect account - no fallback to Prosperia's account
        if (companyError || !company?.stripe_connect_account_id) {
          throw new Error('Payment processing not set up. Please set up your Stripe Connect account in Settings > Payment Processing to accept payments.')
        }

        // Check payment_processing_enabled (this accounts for all restriction factors including capability status)
        if (!company.payment_processing_enabled) {
          throw new Error('Your Stripe Connect account is not yet ready to accept payments. Please complete the onboarding process in Settings > Payment Processing.')
        }
      }

      // For public (unauthenticated) clients, let the Edge Function handle the checks
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-create-connect-checkout`
      
      // Prepare headers - include auth token if available, otherwise Edge Function will use service role
      const headers = { 'Content-Type': 'application/json' }
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`
      } else {
        // For public access, include the anon key as fallback
        headers['Authorization'] = `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      }
      
      // publicToken was already extracted above - it's the magic link token, not invoice.public_token column
      
      const res = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          invoiceId: invoice.id,
          companyId: companyId,
          publicToken: publicToken, // From magic link, not invoice column
          lineItems: invoice.items || [],
          currency: 'usd',
          ...options
        })
      })
      const json = await res.json()
      if (!res.ok) {
        const stripeMsg = json.stripeError?.message || json.details
        throw new Error(stripeMsg ? `${json.error || 'Checkout session failed'}: ${stripeMsg}` : (json.error || 'Failed to create checkout session'))
      }
      return json
    } catch (error) {
      handleSupabaseError(error, 'create invoice checkout session')
      return null
    }
  }

  // Send email via Edge Function (DEPRECATED - Use emailService instead)
  async sendEmail({ to, subject, html, text }) {
    try {
      console.warn('supabaseData.sendEmail is deprecated. Use emailService instead.')
      const result = await clientEmailService.sendEmail({ to, subject, html, text })
      return result.success ? result : null
    } catch (error) {
      handleSupabaseError(error, 'send email')
      return null
    }
  }

  // Send SMS via Edge Function
  // clientId is required: send-sms checks that client's consent and number (#481).
  async sendSms({ to, body, clientId }) {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('No active session')
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-sms`
      const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` }, body: JSON.stringify({ to, body, clientId }) })
      const json = await res.json()
      if (!res.ok) throw new Error(json.error || 'SMS send failed')
      return json
    } catch (error) {
      handleSupabaseError(error, 'send sms')
      return null
    }
  }

  // Log message in message_logs
  async createMessageLog(rec) {
    try {
      const { data, error } = await supabase.from('message_logs').insert([rec]).select().single()
      if (error) throw error
      return data
    } catch (error) {
      handleSupabaseError(error, 'create message log')
      return null
    }
  }

  // Payment management
  async getPayments(passthroughCompanyId = null) {
    try {
      const user = await this.getCurrentUser()
      if (!user && !passthroughCompanyId) throw new Error('User not authenticated')
      
      const companyId = passthroughCompanyId || await getCompanyId(user)
      const { data, error } = await withCompanyFilter(
        supabase.from('payments').select(`
          *,
          invoice:invoices(*)
        `),
        companyId
      )
      
      if (error) throw error
      return data || []
    } catch (error) {
      handleSupabaseError(error, 'get payments')
      return [] // Always return an empty array on error
    }
  }

  async createPayment(paymentData) {
    try {
      // Extract invoice object if passed (for created_by)
      const invoice = paymentData._invoice
      delete paymentData._invoice // Remove helper field
      
      // Transform camelCase to snake_case and handle schema requirements
      const convertedData = {}
      
      // Required fields
      if (paymentData.invoice_id !== undefined) convertedData.invoice_id = paymentData.invoice_id
      if (paymentData.invoiceId !== undefined) convertedData.invoice_id = paymentData.invoiceId
      
      if (paymentData.company_id !== undefined) convertedData.company_id = paymentData.company_id
      if (paymentData.companyId !== undefined) convertedData.company_id = paymentData.companyId
      
      // Get company_id from invoice if not provided
      if (!convertedData.company_id && invoice) {
        convertedData.company_id = invoice.company_id || invoice.companyId
      }
      
      // Get created_by from invoice if available, otherwise try to get from auth user
      if (invoice && (invoice.created_by || invoice.createdBy)) {
        convertedData.created_by = invoice.created_by || invoice.createdBy
      } else {
        // Try to get from authenticated user
        try {
          const user = await this.getCurrentUser()
          if (user) {
            convertedData.created_by = user.id
          }
        } catch (authError) {
          // If no user, we'll need to handle this - for public payments, use invoice created_by or null
          console.warn('No authenticated user for payment, trying invoice created_by')
          if (invoice) {
            // Fetch invoice to get created_by if not in invoice object
            try {
              const { data: invoiceData } = await supabase
                .from('invoices')
                .select('created_by, company_id')
                .eq('id', convertedData.invoice_id)
                .single()
              
              if (invoiceData) {
                convertedData.created_by = invoiceData.created_by
                if (!convertedData.company_id) {
                  convertedData.company_id = invoiceData.company_id
                }
              }
            } catch (fetchError) {
              console.error('Failed to fetch invoice for created_by:', fetchError)
            }
          }
        }
      }
      
      // If we still don't have created_by, try fetching from invoice_id
      if (!convertedData.created_by && convertedData.invoice_id) {
        try {
          const { data: invoiceData } = await supabase
            .from('invoices')
            .select('created_by, company_id')
            .eq('id', convertedData.invoice_id)
            .single()
          
          if (invoiceData) {
            convertedData.created_by = invoiceData.created_by
            if (!convertedData.company_id) {
              convertedData.company_id = invoiceData.company_id
            }
          }
        } catch (fetchError) {
          console.error('Failed to fetch invoice for created_by:', fetchError)
        }
      }
      
      // Amount (required) - assign before validation
      if (paymentData.amount !== undefined) convertedData.amount = paymentData.amount
      
      // Validate required fields
      if (!convertedData.company_id) {
        throw new Error('company_id is required for payment')
      }
      if (!convertedData.created_by) {
        throw new Error('created_by is required for payment. Please ensure the invoice has a valid created_by field.')
      }
      if (!convertedData.invoice_id) {
        throw new Error('invoice_id is required for payment')
      }
      if (convertedData.amount === undefined || convertedData.amount === null) {
        throw new Error('amount is required for payment')
      }
      
      // Optional fields with transformation
      if (paymentData.payment_method !== undefined) convertedData.payment_method = paymentData.payment_method
      if (paymentData.method !== undefined) convertedData.payment_method = paymentData.method
      
      if (paymentData.status !== undefined) convertedData.status = paymentData.status
      if (paymentData.reference_number !== undefined) convertedData.reference_number = paymentData.reference_number
      if (paymentData.referenceNumber !== undefined) convertedData.reference_number = paymentData.referenceNumber
      
      if (paymentData.notes !== undefined) convertedData.notes = paymentData.notes
      if (paymentData.processor_id !== undefined) convertedData.processor_id = paymentData.processor_id
      if (paymentData.processorId !== undefined) convertedData.processor_id = paymentData.processorId
      
      if (paymentData.metadata !== undefined) convertedData.metadata = paymentData.metadata
      if (paymentData.stripe_connect_account_id !== undefined) convertedData.stripe_connect_account_id = paymentData.stripe_connect_account_id
      if (paymentData.stripeConnectAccountId !== undefined) convertedData.stripe_connect_account_id = paymentData.stripeConnectAccountId
      
      if (paymentData.application_fee_amount !== undefined) convertedData.application_fee_amount = paymentData.application_fee_amount
      if (paymentData.applicationFeeAmount !== undefined) convertedData.application_fee_amount = paymentData.applicationFeeAmount
      
      if (paymentData.connect_account_type !== undefined) convertedData.connect_account_type = paymentData.connect_account_type
      if (paymentData.connectAccountType !== undefined) convertedData.connect_account_type = paymentData.connectAccountType
      
      // Remove clientId if present (not in payments table)
      // All camelCase fields have been converted above, so no need to explicitly remove
      
      const { data, error } = await supabase
        .from('payments')
        .insert([convertedData])
        .select()
        .single()
      
      if (error) throw error
      return data
    } catch (error) {
      handleSupabaseError(error, 'create payment')
      return null // Return null on error
    }
  }

  async getPayment(id) {
    try {
      const user = await this.getCurrentUser()
      if (!user) throw new Error('User not authenticated')
      
      const companyId = await getCompanyId(user)
      const { data, error } = await withCompanyFilter(
        supabase.from('payments').select(`
          *,
          invoice:invoices(*)
        `).eq('id', id),
        companyId
      ).single()
      
      if (error) throw error
      return data
    } catch (error) {
      handleSupabaseError(error, 'get payment')
      return null // Return null on error for single item
    }
  }

  // Job time tracking — event-based check-in/check-out log.
  // Each row is one event; the latest event for (user, job) determines whether
  // the user is currently checked in.
  async getJobTimeLogs(jobId, userId) {
    try {
      const user = await this.getCurrentUser()
      if (!user) throw new Error('User not authenticated')
      const { data, error } = await supabase
        .from('job_time_logs')
        .select('*')
        .eq('job_id', jobId)
        .eq('user_id', userId || user.id)
        .order('occurred_at', { ascending: true })
      if (error) throw error
      return data || []
    } catch (error) {
      handleSupabaseError(error, 'get job time logs')
      return []
    }
  }

  async checkInToJob(jobId, payload = {}) {
    try {
      const user = await this.getCurrentUser()
      if (!user) throw new Error('User not authenticated')
      const companyId = await getCompanyId(user)
      const { data, error } = await supabase
        .from('job_time_logs')
        .insert([{
          job_id: jobId,
          user_id: payload.userId || user.id,
          company_id: companyId,
          type: 'checkin',
          occurred_at: payload.checkinTime || new Date().toISOString(),
          location: payload.location || null,
        }])
        .select()
        .single()
      if (error) throw error
      return data
    } catch (error) {
      handleSupabaseError(error, 'check in to job')
      throw error
    }
  }

  async checkOutFromJob(jobId, payload = {}) {
    try {
      const user = await this.getCurrentUser()
      if (!user) throw new Error('User not authenticated')
      const companyId = await getCompanyId(user)
      const { data, error } = await supabase
        .from('job_time_logs')
        .insert([{
          job_id: jobId,
          user_id: payload.userId || user.id,
          company_id: companyId,
          type: 'checkout',
          occurred_at: payload.checkoutTime || new Date().toISOString(),
          location: payload.location || null,
        }])
        .select()
        .single()
      if (error) throw error
      return data
    } catch (error) {
      handleSupabaseError(error, 'check out from job')
      throw error
    }
  }

  // Task management
  async createTask(taskData) {
    try {
      // Validate required fields
      if (!taskData.createdBy && !taskData.created_by) {
        throw new Error('createdBy field is required for task creation')
      }

      // Map camelCase to snake_case for database compatibility
      const mappedData = {
        company_id: taskData.companyId || taskData.company_id,
        client_id: taskData.clientId || taskData.client_id,
        assigned_to: taskData.assigneeId || taskData.assigned_to,
        title: taskData.title,
        description: taskData.description,
        status: taskData.status || 'pending',
        priority: taskData.priority || 'medium',
        due_date: taskData.dueDate || taskData.due_date,
        related_type: taskData.relatedType || taskData.related_type,
        related_id: taskData.relatedId || taskData.related_id,
        created_by: taskData.createdBy || taskData.created_by
      }

      const { data, error } = await supabase
        .from('tasks')
        .insert([mappedData])
        .select()
        .single()
      
      if (error) throw error
      
      // Convert snake_case to camelCase for frontend consistency
      return {
        id: data.id,
        companyId: data.company_id,
        clientId: data.client_id,
        assigneeId: data.assigned_to,
        title: data.title,
        description: data.description,
        status: data.status,
        priority: data.priority,
        dueDate: data.due_date,
        completedAt: data.completed_at,
        relatedType: data.related_type,
        relatedId: data.related_id,
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at
      }
    } catch (error) {
      handleSupabaseError(error, 'create task')
      return null // Return null on error
    }
  }

  async getTasks() {
    try {
      const user = await this.getCurrentUser()
      if (!user) throw new Error('User not authenticated')
      
      const companyId = await getCompanyId(user)
      const { data, error } = await withCompanyFilterCamelCase(
        supabase.from('tasks').select('*'),
        companyId
      )
      
      if (error) throw error
      
      // Convert snake_case to camelCase for frontend consistency
      const camelCaseData = (data || []).map(task => ({
        id: task.id,
        companyId: task.company_id,
        clientId: task.client_id,
        assigneeId: task.assigned_to,
        title: task.title,
        description: task.description,
        status: task.status,
        priority: task.priority,
        dueDate: task.due_date,
        completedAt: task.completed_at,
        relatedType: task.related_type,
        relatedId: task.related_id,
        createdBy: task.created_by,
        createdAt: task.created_at,
        updatedAt: task.updated_at
      }))
      
      return camelCaseData
    } catch (error) {
      handleSupabaseError(error, 'get tasks')
      return [] // Always return an empty array on error
    }
  }

  async updateTask(id, updates) {
    try {
      const user = await this.getCurrentUser()
      if (!user) throw new Error('User not authenticated')
      
      const companyId = await getCompanyId(user)
      
      // Map camelCase to snake_case for database compatibility
      const mappedUpdates = {}
      if (updates.clientId !== undefined) mappedUpdates.client_id = updates.clientId
      if (updates.assigneeId !== undefined) mappedUpdates.assigned_to = updates.assigneeId
      if (updates.dueDate !== undefined) mappedUpdates.due_date = updates.dueDate
      if (updates.relatedType !== undefined) mappedUpdates.related_type = updates.relatedType
      if (updates.relatedId !== undefined) mappedUpdates.related_id = updates.relatedId
      if (updates.title !== undefined) mappedUpdates.title = updates.title
      if (updates.description !== undefined) mappedUpdates.description = updates.description
      if (updates.status !== undefined) mappedUpdates.status = updates.status
      if (updates.priority !== undefined) mappedUpdates.priority = updates.priority
      
      const { data, error } = await withCompanyFilterCamelCase(
        supabase.from('tasks').update(mappedUpdates).eq('id', id),
        companyId
      ).select().single()
      
      if (error) throw error
      
      // Convert snake_case to camelCase for frontend consistency
      return {
        id: data.id,
        companyId: data.company_id,
        clientId: data.client_id,
        assigneeId: data.assigned_to,
        title: data.title,
        description: data.description,
        status: data.status,
        priority: data.priority,
        dueDate: data.due_date,
        completedAt: data.completed_at,
        relatedType: data.related_type,
        relatedId: data.related_id,
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at
      }
    } catch (error) {
      handleSupabaseError(error, 'update task')
      return null // Return null on error
    }
  }

  async deleteTask(id) {
    try {
      const user = await this.getCurrentUser()
      if (!user) throw new Error('User not authenticated')
      
      const companyId = await getCompanyId(user)
      const { error } = await withCompanyFilterCamelCase(
        supabase.from('tasks').delete().eq('id', id),
        companyId
      )
      
      if (error) throw error
      return true
    } catch (error) {
      handleSupabaseError(error, 'delete task')
      return false // Return false on error
    }
  }

  async getTask(id) {
    try {
      const user = await this.getCurrentUser()
      if (!user) throw new Error('User not authenticated')
      
      const companyId = await getCompanyId(user)
      const { data, error } = await withCompanyFilterCamelCase(
        supabase.from('tasks').select('*').eq('id', id),
        companyId
      ).single()
      
      if (error) throw error
      return data
    } catch (error) {
      handleSupabaseError(error, 'get task')
      return null // Return null on error for single item
    }
  }

  // Employee management
  async getEmployees(passthroughCompanyId = null) {
    try {
      const user = await this.getCurrentUser()
      if (!user && !passthroughCompanyId) throw new Error('User not authenticated')
      
      const companyId = passthroughCompanyId || await getCompanyId(user)
      if (!companyId) throw new Error('Company not available')
      
      const cacheKey = `employees_${companyId}`
      return await this.getCachedOrRateLimited(cacheKey, async () => {
        // RLS policies will automatically filter by company_id
        const { data, error } = await supabase
          .from('employees')
          .select('*')
          .order('created_at', { ascending: false })
        
        if (error) {
          console.error('[supabaseData] Error fetching employees:', error)
          throw error
        }
        
        return data || []
      })
    } catch (error) {
      console.error('[supabaseData] getEmployees error:', error)
      handleSupabaseError(error, 'fetch employees')
      return []
    }
  }

  async getExpenses(passthroughCompanyId = null) {
    try {
      const user = await this.getCurrentUser()
      if (!user && !passthroughCompanyId) throw new Error('User not authenticated')
      
      const companyId = passthroughCompanyId || await getCompanyId(user)
      if (!companyId) throw new Error('Company not available')
      
      const cacheKey = `expenses_${companyId}`
      return await this.getCachedOrRateLimited(cacheKey, async () => {
        const { data, error } = await supabase
          .from('expenses')
          .select('*')
          .eq('company_id', companyId)
          .order('created_at', { ascending: false })
          .limit(500)

        if (error) {
          console.error('[supabaseData] Error fetching expenses:', error)
          throw error
        }
        
        return data || []
      })
    } catch (error) {
      console.error('[supabaseData] getExpenses error:', error)
      handleSupabaseError(error, 'fetch expenses')
      return []
    }
  }

  async createEmployee(employeeData) {
    try {
      const { data, error } = await supabase
        .from('employees')
        .insert([employeeData])
        .select()
        .single()
      
      if (error) throw error
      return data
    } catch (error) {
      handleSupabaseError(error, 'create employee')
      return null // Return null on error
    }
  }

  async deleteEmployee(id) {
    try {
      const user = await this.getCurrentUser()
      if (!user) throw new Error('User not authenticated')
      
      const companyId = await getCompanyId(user)
      if (!companyId) throw new Error('Company not available')
      
      const { error } = await supabase
        .from('employees')
        .delete()
        .eq('id', id)
        .eq('company_id', companyId)
      
      if (error) throw error
      return true
    } catch (error) {
      handleSupabaseError(error, 'delete employee')
      return false // Return false on error
    }
  }

  async getEmployee(id) {
    try {
      const user = await this.getCurrentUser()
      if (!user) throw new Error('User not authenticated')
      
      const companyId = await getCompanyId(user)
      if (!companyId) throw new Error('Company not available')
      
      const { data, error } = await supabase
        .from('employees')
        .select('*')
        .eq('id', id)
        .eq('company_id', companyId)
        .single()
      
      if (error) throw error
      return data
    } catch (error) {
      handleSupabaseError(error, 'get employee')
      return null // Return null on error for single item
    }
  }

  // ============================================
  // Real-time subscriptions (entity-specific)
  // Note: subscribeToTable, unsubscribeFromTable, clearAllSubscriptions
  // are inherited from DataServiceCore
  // ============================================

  // Specific subscription methods for Dashboard
  subscribeToClients(callback) {
    return this.subscribeToTable('clients', '*', callback)
  }

  subscribeToJobs(callback) {
    return this.subscribeToTable('jobs', '*', callback)
  }

  subscribeToEstimates(callback) {
    // Get current company ID for filtering
    return this.getCurrentUser().then(user => {
      if (!user) {
        console.warn('[supabaseData] No user found for estimates subscription')
        return null
      }
      return getCompanyId(user).then(companyId => {
        if (!companyId) {
          console.warn('[supabaseData] No company ID found for estimates subscription')
          return null
        }
        // Subscribe with company filter
        return this.subscribeToTable('estimates', '*', callback, { company_id: companyId })
      })
    }).catch(error => {
      console.error('[supabaseData] Error setting up estimates subscription:', error)
      return null
    })
  }

  subscribeToInvoices(callback) {
    return this.subscribeToTable('invoices', '*', callback)
  }

  subscribeToPayments(callback) {
    return this.subscribeToTable('payments', '*', callback)
  }

  // Specific unsubscribe methods for Dashboard
  unsubscribeFromClients() {
    this.unsubscribeFromTable('clients', '*')
  }

  unsubscribeFromJobs() {
    this.unsubscribeFromTable('jobs', '*')
  }

  unsubscribeFromEstimates() {
    this.unsubscribeFromTable('estimates', '*')
  }

  unsubscribeFromInvoices() {
    this.unsubscribeFromTable('invoices', '*')
  }

  unsubscribeFromPayments() {
    this.unsubscribeFromTable('payments', '*')
  }

  // Note: unsubscribeFromTable and clearAllSubscriptions are inherited from DataServiceCore

  // Dashboard and reporting
  async getDashboardData() {
    try {
      const user = await this.getCurrentUser()
      if (!user) throw new Error('User not authenticated')
      
      const companyId = await getCompanyId(user)
      
      // Get counts for dashboard
      const [clients, jobs, estimates, invoices, payments] = await Promise.all([
        this.getClients(),
        this.getJobs(),
        this.getEstimates(),
        this.getInvoices(),
        this.getPayments()
      ])
      
      // Calculate totals
      const totalRevenue = payments.reduce((sum, payment) => sum + (payment.amount || 0), 0)
      const pendingEstimates = estimates.filter(est => est.status === 'sent' || est.status === 'viewed').length
      const activeJobs = jobs.filter(job => job.status === 'in_progress').length
      
      return {
        totalClients: clients.length,
        totalJobs: jobs.length,
        totalEstimates: estimates.length,
        totalInvoices: invoices.length,
        totalRevenue,
        pendingEstimates,
        activeJobs
      }
    } catch (error) {
      handleSupabaseError(error, 'get dashboard data')
      return {
        totalClients: 0,
        totalJobs: 0,
        totalEstimates: 0,
        totalInvoices: 0,
        totalRevenue: 0,
        pendingEstimates: 0,
        activeJobs: 0
      } // Return default values on error
    }
  }

  // File storage
  async uploadFile(file, path) {
    try {
      const { data, error } = await supabase.storage
        .from('files')
        .upload(path, file, { upsert: false })
      
      if (error) throw error
      // Return a public URL for immediate use in the app
      const { data: pub } = await supabase.storage
        .from('files')
        .getPublicUrl(path)
      return pub?.publicUrl || null
    } catch (error) {
      handleSupabaseError(error, 'upload file')
      return null // Return null on error
    }
  }

  async getFileUrl(path) {
    try {
      const { data, error } = await supabase.storage
        .from('files')
        .getPublicUrl(path)
      
      if (error) throw error
      return data.publicUrl
    } catch (error) {
      handleSupabaseError(error, 'get file URL')
      return null // Return null on error
    }
  }

  async deleteFile(path) {
    try {
      const { error } = await supabase.storage
        .from('files')
        .remove([path])
      
      if (error) throw error
      return true
    } catch (error) {
      handleSupabaseError(error, 'delete file')
      return false // Return false on error
    }
  }

  // ================================================
  // Toolbelt preferences
  // ================================================
  async getToolbeltPreferences(userId) {
    try {
      if (!userId) return []
      const { data, error } = await supabase
        .from('user_toolbelt_preferences')
        .select('selected_tools')
        .eq('user_id', userId)
        .single()
      
      if (error) {
        // PGRST116 = No rows found
        if (error.code === 'PGRST116') return []
        throw error
      }
      const tools = Array.isArray(data?.selected_tools) ? data.selected_tools : []
      return tools
    } catch (error) {
      handleSupabaseError(error, 'get toolbelt preferences')
      return []
    }
  }

  async saveToolbeltPreferences(userId, tools) {
    try {
      if (!userId) return { success: false }
      const safeTools = Array.isArray(tools) ? tools : []
      const { data, error } = await supabase
        .from('user_toolbelt_preferences')
        .upsert({ user_id: userId, selected_tools: safeTools }, { onConflict: 'user_id' })
        .select('selected_tools')
        .single()
      if (error) throw error
      return { success: true, tools: Array.isArray(data?.selected_tools) ? data.selected_tools : safeTools }
    } catch (error) {
      handleSupabaseError(error, 'save toolbelt preferences')
      return { success: false }
    }
  }

  // ================================================
  // Dashboard preferences
  // ================================================
  async getDashboardPreferences(userId) {
    try {
      if (!userId) return { tabs: [], activeTab: 'business-health' }
      const { data, error } = await supabase
        .from('user_dashboard_preferences')
        .select('tabs, active_tab, layouts_by_tab')
        .eq('user_id', userId)
        .single()
      
      if (error) {
        // PGRST116 = No rows found
        if (error.code === 'PGRST116') {
          return { 
            tabs: [
              { id: 'business-health', name: 'Business Health Metrics', isDefault: true },
              { id: 'financials', name: 'Financials', isDefault: true },
              { id: 'field', name: 'Field', isDefault: true }
            ], 
            activeTab: 'business-health' 
          }
        }
        throw error
      }
      
      const tabs = Array.isArray(data?.tabs) ? data.tabs : [
        { id: 'business-health', name: 'Business Health Metrics', isDefault: true },
        { id: 'financials', name: 'Financials', isDefault: true },
        { id: 'field', name: 'Field', isDefault: true }
      ]
      const activeTab = data?.active_tab || 'business-health'
      const layoutsByTab = (data?.layouts_by_tab && typeof data.layouts_by_tab === 'object') ? data.layouts_by_tab : {}
      
      return { tabs, activeTab, layoutsByTab }
    } catch (error) {
      handleSupabaseError(error, 'get dashboard preferences')
      return { 
        tabs: [
          { id: 'business-health', name: 'Business Health Metrics', isDefault: true },
          { id: 'financials', name: 'Financials', isDefault: true },
          { id: 'field', name: 'Field', isDefault: true }
        ], 
        activeTab: 'business-health' 
      }
    }
  }

  async saveDashboardPreferences(userId, tabs, activeTab, layoutsByTab = {}) {
    try {
      if (!userId) return { success: false }
      const safeTabs = Array.isArray(tabs) ? tabs : []
      const { data, error } = await supabase
        .from('user_dashboard_preferences')
        .upsert({ 
          user_id: userId, 
          tabs: safeTabs, 
          active_tab: activeTab || 'business-health',
          layouts_by_tab: layoutsByTab
        }, { onConflict: 'user_id' })
        .select('tabs, active_tab, layouts_by_tab')
        .single()
      if (error) throw error
      return {
        success: true,
        tabs: Array.isArray(data?.tabs) ? data.tabs : safeTabs,
        activeTab: data?.active_tab || activeTab,
        layoutsByTab: (data?.layouts_by_tab && typeof data.layouts_by_tab === 'object') ? data.layouts_by_tab : layoutsByTab
      }
    } catch (error) {
      handleSupabaseError(error, 'save dashboard preferences')
      return { success: false }
    }
  }

  // ============================================
  // Job assignments
  // ============================================
  async checkAssignmentConflict(employeeId, startTime, endTime, excludeJobId) {
    const { data, error } = await supabase.rpc('check_assignment_conflicts', {
      p_employee_id: employeeId,
      p_start_time: startTime,
      p_end_time: endTime,
      p_exclude_job_id: excludeJobId,
    })
    if (error) throw error
    return data || []
  }

  async getJobAssignments(jobId) {
    const { data, error } = await supabase
      .from('job_assignments')
      .select('id, employee_id, source, team_id')
      .eq('job_id', jobId)
    if (error) throw error
    return data || []
  }

  async setJobAssignments(jobId, newEmployeeIds, teamId, currentAssignments) {
    const currentIds = currentAssignments.map(a => a.employee_id)
    const toAdd = newEmployeeIds.filter(id => !currentIds.includes(id))
    const toRemove = currentIds.filter(id => !newEmployeeIds.includes(id))
    const ops = []
    if (toRemove.length > 0) {
      const idsToDelete = currentAssignments
        .filter(a => toRemove.includes(a.employee_id))
        .map(a => a.id)
      if (idsToDelete.length > 0) {
        ops.push(supabase.from('job_assignments').delete().in('id', idsToDelete))
      }
    }
    if (toAdd.length > 0) {
      ops.push(supabase.from('job_assignments').insert(
        toAdd.map(empId => ({
          job_id: jobId,
          employee_id: empId,
          assigned_by: null,
          source: teamId ? 'team' : 'direct',
          team_id: teamId || null,
        }))
      ))
    }
    await Promise.all(ops)
    return this.getJobAssignments(jobId)
  }

  // ============================================
  // Teams (team_tiers + team_tier_members cascade via FK)
  // ============================================
  async getTeamsWithMembers({ orderBy = 'name', ascending = true } = {}) {
    const { data, error } = await supabase
      .from('teams')
      .select('id, name, description, created_at, tiers:team_tiers(id, name, sort_order, is_supervisory, members:team_tier_members(id, employee_id))')
      .order(orderBy, { ascending })
    if (error) throw error
    return data || []
  }

  async saveTeam({ teamId, name, description, tiers, companyId, userId }) {
    let id = teamId
    if (id) {
      const { error } = await supabase
        .from('teams')
        .update({ name, description, updated_at: new Date().toISOString() })
        .eq('id', id)
      if (error) throw error
      // CASCADE wipes team_tier_members along with their tiers
      await supabase.from('team_tiers').delete().eq('team_id', id)
    } else {
      const { data, error } = await supabase
        .from('teams')
        .insert({ name, description, company_id: companyId, created_by: userId })
        .select()
        .single()
      if (error) throw error
      id = data.id
    }
    for (let i = 0; i < tiers.length; i++) {
      const tier = tiers[i]
      const { data: newTier, error: tierErr } = await supabase
        .from('team_tiers')
        .insert({
          team_id: id,
          name: tier.name || `Tier ${i + 1}`,
          is_supervisory: tier.is_supervisory,
          sort_order: i,
        })
        .select()
        .single()
      if (tierErr) throw tierErr
      if (tier.memberIds?.length > 0) {
        const { error: memberErr } = await supabase
          .from('team_tier_members')
          .insert(tier.memberIds.map(empId => ({
            team_tier_id: newTier.id,
            employee_id: empId,
          })))
        if (memberErr) throw memberErr
      }
    }
    return id
  }

  async deleteTeam(teamId) {
    // CASCADE handles team_tiers and team_tier_members
    const { error } = await supabase.from('teams').delete().eq('id', teamId)
    if (error) throw error
  }
}

// Create and export singleton instance
const supabaseDataInstance = new SupabaseDataService()
export const supabaseData = supabaseDataInstance
export default supabaseDataInstance

// Backward compatibility aliases
export const listClients = (...args) => supabaseData.getClients(...args)
export const createClient = (...args) => supabaseData.createClient(...args)
export const updateClient = (...args) => supabaseData.updateClient(...args)
export const deleteClient = (...args) => supabaseData.deleteClient(...args)
export const getClient = (...args) => supabaseData.getClient(...args)

// Job aliases
export const listJobs = (...args) => supabaseData.getJobs(...args)
export const createJob = (...args) => supabaseData.createJob(...args)
export const updateJob = (...args) => supabaseData.updateJob(...args)
export const deleteJob = (...args) => supabaseData.deleteJob(...args)
export const getJob = (...args) => supabaseData.getJob(...args)
export const getJobsByClient = (...args) => supabaseData.getJobsByClient(...args)

// Estimate aliases
export const listEstimates = (...args) => supabaseData.getEstimates(...args)
export const createEstimate = (...args) => supabaseData.createEstimate(...args)
export const updateEstimate = (...args) => supabaseData.updateEstimate(...args)
export const getEstimate = (...args) => supabaseData.getEstimate(...args)
export const generateEstimateNumber = (...args) => supabaseData.generateEstimateNumber(...args)

// Invoice aliases
export const listInvoices = (...args) => supabaseData.getInvoices(...args)
export const createInvoice = (...args) => supabaseData.createInvoice(...args)
export const updateInvoice = (...args) => supabaseData.updateInvoice(...args)
export const deleteInvoice = (...args) => supabaseData.deleteInvoice(...args)
export const getInvoice = (...args) => supabaseData.getInvoice(...args)

// Payment aliases
export const listPayments = (...args) => supabaseData.getPayments(...args)
export const createPayment = (...args) => supabaseData.createPayment(...args)
export const getPayment = (...args) => supabaseData.getPayment(...args)

// Employee aliases
export const listEmployees = (...args) => supabaseData.getEmployees(...args)
export const createEmployee = (...args) => supabaseData.createEmployee(...args)
export const deleteEmployee = (...args) => supabaseData.deleteEmployee(...args)
export const getEmployee = (...args) => supabaseData.getEmployee(...args)

// Task aliases
export const listTasks = (...args) => supabaseData.getTasks(...args)
export const createTask = (...args) => supabaseData.createTask(...args)
export const updateTask = (...args) => supabaseData.updateTask(...args)
export const deleteTask = (...args) => supabaseData.deleteTask(...args)
export const getTask = (...args) => supabaseData.getTask(...args)

// Company aliases
export const getCompany = (...args) => supabaseData.getCompany(...args)
export const updateCompany = (...args) => supabaseData.updateCompany(...args)

// Dashboard aliases
export const getDashboardData = (...args) => supabaseData.getDashboardData(...args)

// Expense aliases
export const getExpenses = (...args) => supabaseData.getExpenses(...args)

// File storage aliases
export const uploadFile = (...args) => supabaseData.uploadFile(...args)
export const getFileUrl = (...args) => supabaseData.getFileUrl(...args)
export const deleteFile = (...args) => supabaseData.deleteFile(...args)

// Subscription aliases
export const subscribeToClients = (...args) => supabaseData.subscribeToClients(...args)
export const subscribeToJobs = (...args) => supabaseData.subscribeToJobs(...args)
export const subscribeToEstimates = (...args) => supabaseData.subscribeToEstimates(...args)
export const subscribeToInvoices = (...args) => supabaseData.subscribeToInvoices(...args)
export const subscribeToPayments = (...args) => supabaseData.subscribeToPayments(...args)

export const unsubscribeFromClients = (...args) => supabaseData.unsubscribeFromClients(...args)
export const unsubscribeFromJobs = (...args) => supabaseData.unsubscribeFromJobs(...args)
export const unsubscribeFromEstimates = (...args) => supabaseData.unsubscribeFromEstimates(...args)
export const unsubscribeFromInvoices = (...args) => supabaseData.unsubscribeFromInvoices(...args)
export const unsubscribeFromPayments = (...args) => supabaseData.unsubscribeFromPayments(...args) 

// Toolbelt aliases
export const getToolbeltPreferences = (...args) => supabaseData.getToolbeltPreferences(...args)
export const saveToolbeltPreferences = (...args) => supabaseData.saveToolbeltPreferences(...args)

// Dashboard aliases
export const getDashboardPreferences = (...args) => supabaseData.getDashboardPreferences(...args)
export const saveDashboardPreferences = (...args) => supabaseData.saveDashboardPreferences(...args)